package com.example.mystybag.login.views

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import com.example.mystybag.common.extensions.Image
import com.example.mystybag.common.extensions.noRippleClickable
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.ui.components.custom.DefaultCircleShapedTextField
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.ui.views.BottomSheetUIView
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.login.logic.StaffResetPasswordViewModel
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p6
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p32
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import dev.icerock.moko.resources.compose.painterResource
import dev.icerock.moko.resources.compose.stringResource

@Composable
fun StaffResetPasswordView(viewModel: StaffResetPasswordViewModel) {
    val keyboardController = LocalSoftwareKeyboardController.current
    val uiState = viewModel.uiState.subscribeAsState()
    val theme = AppTheme.current

    BottomSheetUIView(
        viewModel = viewModel,
        bottomSheetFactory = {}
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .weight(1f)
                .padding(horizontal = p16),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(top = p32, bottom = p24),
                contentAlignment = Alignment.CenterStart
            ) {
                Texts.DisplaySmall(
                    text = stringResource(SR.strings.change_password),
                    color = theme.colors.onSurface,
                )
            }

            Row(
                modifier = Modifier
                    .fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(p6, alignment = Alignment.CenterHorizontally)
            ) {
                DefaultCircleShapedTextField(
                    modifier = Modifier.fillMaxWidth(),
                    value = uiState.value.temporaryPassword,
                    label = stringResource(SR.strings.temporary_password),
                    errorText = uiState.value.temporaryPasswordErrorMsg,
                    errorLeadingIcon = SRC.images.password_error_icon,
                    onValueChange = viewModel::onTemporaryPasswordChange,
                    isError = uiState.value.temporaryPasswordIsError,
                    visualTransformation = if (uiState.value.temporaryPasswordVisible) VisualTransformation.None else PasswordVisualTransformation(),
                    trailingIcon = {
                        Image(
                            painter = painterResource(
                                if (uiState.value.temporaryPasswordVisible) SRC.images.password_eye_open else SRC.images.password_eye_closed
                            ),
                            modifier = Modifier.size(p24).noRippleClickable { viewModel.toggleTemporaryPasswordVisibility() }
                        )
                    },
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Ascii, imeAction = ImeAction.Next)
                )
            }

            Row(
                modifier = Modifier.padding(top = p16).fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(p6, alignment = Alignment.CenterHorizontally)
            ) {
                DefaultCircleShapedTextField(
                    modifier = Modifier.fillMaxWidth(),
                    value = uiState.value.newPassword,
                    label = stringResource(SR.strings.new_password),
                    errorText = uiState.value.newPasswordErrorMsg,
                    errorLeadingIcon = SRC.images.password_error_icon,
                    onValueChange = viewModel::onNewPasswordChange,
                    isError = uiState.value.newPasswordIsError,
                    visualTransformation = if (uiState.value.newPasswordVisible) VisualTransformation.None else PasswordVisualTransformation(),
                    trailingIcon = {
                        Image(
                            painter = painterResource(
                                if (uiState.value.newPasswordVisible) SRC.images.password_eye_open else SRC.images.password_eye_closed
                            ),
                            modifier = Modifier.size(p24).noRippleClickable { viewModel.toggleNewPasswordVisibility() }
                        )
                    },
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Ascii, imeAction = ImeAction.Next)
                )
            }

            Row(
                modifier = Modifier.padding(top = p16).fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(p6, alignment = Alignment.CenterHorizontally)
            ) {
                DefaultCircleShapedTextField(
                    modifier = Modifier.fillMaxWidth(),
                    value = uiState.value.confirmPassword,
                    label = stringResource(SR.strings.confirm_password),
                    errorText = uiState.value.confirmPasswordErrorMsg,
                    errorLeadingIcon = SRC.images.password_error_icon,
                    onValueChange = viewModel::onConfirmPasswordChange,
                    isError = uiState.value.confirmPasswordIsError,
                    visualTransformation = if (uiState.value.confirmPasswordVisible) VisualTransformation.None else PasswordVisualTransformation(),
                    trailingIcon = {
                        Image(
                            painter = painterResource(
                                if (uiState.value.confirmPasswordVisible) SRC.images.password_eye_open else SRC.images.password_eye_closed
                            ),
                            modifier = Modifier.size(p24).noRippleClickable { viewModel.toggleConfirmPasswordVisibility() }
                        )
                    },
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Ascii, imeAction = ImeAction.Done)
                )
            }

            Spacer(Modifier.weight(1f))

            DefaultButton(
                modifier = Modifier.fillMaxWidth().padding(bottom = p8),
                caption = stringResource(SR.strings.save),
                callback = {
                    keyboardController?.hide()
                    viewModel.onContinue()
                },
                identifier = "Staff Reset Password Continue"
            )
        }
    }
}
